// Resolves reference tokens to the labels their badges show. Badges register
// ids with a shared per-organization store and one debounced request resolves
// the whole batch; per-badge detail GETs previously herded the API into rate
// limiting, leaving every pill on its fallback label (PHO-17292). A bespoke
// store (not SWR) because badges surface incrementally as the virtualized
// transcript scrolls: the store fetches only the newly seen ids, where an
// SWR key over the accumulated id set would refetch the whole set on every
// newly mounted badge.
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useSyncExternalStore,
} from 'react';

import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { groupReferenceLabel } from '@apps/web/features/phoebe_chat_v3/model/group_reference_label';
import { type ReferenceType } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { type OrganizationEvent } from '@apps/web/hooks/organization_event_schemas';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { getApiClient } from '@apps/web/lib/api_client';
import {
    formatDateWithWeekday,
    formatTime,
    toDateKey,
} from '@apps/web/lib/date_utils';
import { ApiError, type components } from '@libraries/typescript/api_client';

const noop = (): void => undefined;

const FLUSH_DELAY_MS = 50;
const RETRY_DELAY_MS = 30_000;
// Labels refresh in one batch when the window regains focus, at most this
// often, so a renamed record corrects without remounting the chat.
const REFRESH_MIN_INTERVAL_MS = 60_000;
// The resolve endpoint rejects larger id lists outright.
const MAX_IDS_PER_TYPE = 500;

type ResolveResponse = Readonly<
    components['schemas']['EntityReferenceResolveResponse']
>;
type ShiftReferenceRead = components['schemas']['ShiftReferenceRead'];
type GroupMemberReferenceRead =
    components['schemas']['GroupMemberReferenceRead'];

export type ResolvedShiftReference = Readonly<{
    readonly kind: 'shift';
    readonly patientName: string;
    readonly shiftStartTime: string;
    readonly shiftEndTime: string;
    readonly status: ShiftReferenceRead['status'];
    readonly latestStage: ShiftReferenceRead['latest_stage'];
    readonly hasActiveFillOutreach: boolean;
    readonly groupId: string | null;
    readonly groupMode: ShiftReferenceRead['group_mode'];
}>;

export type ResolvedGroupMember = Readonly<{
    readonly shiftId: string;
    readonly clientId: string | null;
    readonly patientName: string;
    readonly shiftStartTime: string;
    readonly shiftEndTime: string;
    readonly status: GroupMemberReferenceRead['status'];
    readonly resolution: GroupMemberReferenceRead['resolution'];
    readonly assignedCaregiverId: string | null;
    readonly assignedCaregiverName: string | null;
}>;

export type ResolvedGroupReference = Readonly<{
    readonly kind: 'group';
    readonly outreachId: string;
    readonly groupMode: 'all' | 'any';
    readonly ordinal: number;
    readonly members: ReadonlyArray<ResolvedGroupMember>;
    readonly openMemberCount: number;
    // Whether the all-or-none constraint still binds; the server owns that
    // rule so the badge cannot disagree with the response handler.
    readonly allOrNoneActive: boolean;
}>;

/** An id the server omitted stays `missing` so it is never refetched. */
export type ResolvedReference =
    | ResolvedShiftReference
    | Readonly<{ readonly kind: 'caregiver'; readonly name: string }>
    | Readonly<{
          readonly kind: 'client';
          readonly firstName: string;
          readonly lastName: string;
      }>
    | Readonly<{
          readonly kind: 'callout';
          readonly shiftCount: number;
          // The first shifts chronologically, server-capped; shiftCount is
          // the true total. Feeds the badge's hover preview.
          readonly shiftIds: ReadonlyArray<string>;
          readonly shiftStartTime: string | null;
          readonly shiftEndTime: string | null;
      }>
    | ResolvedGroupReference
    | Readonly<{ readonly kind: 'playbook'; readonly title: string }>
    | Readonly<{
          readonly kind: 'care_plan';
          readonly firstName: string;
          readonly lastName: string;
      }>
    | Readonly<{ readonly kind: 'caregiver_memory'; readonly name: string }>
    | Readonly<{
          readonly kind: 'client_memory';
          readonly firstName: string;
          readonly lastName: string;
      }>
    | Readonly<{ readonly kind: 'missing' }>;

type EntityReferenceLabelsStore = Readonly<{
    readonly subscribe: (listener: () => void) => () => void;
    readonly get: (
        type: ReferenceType,
        id: string,
    ) => ResolvedReference | undefined;
    readonly request: (type: ReferenceType, id: string) => void;
    readonly refresh: () => void;
    readonly invalidate: (
        matches: (type: ReferenceType, id: string) => boolean,
    ) => void;
    readonly dispose: () => void;
}>;

const REQUEST_FIELD_BY_TYPE: Readonly<Record<ReferenceType, string>> = {
    shift: 'shift_ids',
    caregiver: 'caregiver_ids',
    client: 'client_ids',
    callout: 'outreach_ids',
    group: 'group_ids',
    playbook: 'playbook_ids',
    care_plan: 'care_plan_ids',
    caregiver_memory: 'caregiver_memory_ids',
    client_memory: 'client_memory_ids',
};

function createEntityReferenceLabelsStore({
    organizationId,
    mode,
    flushDelayMs,
    retryDelayMs,
    refreshMinIntervalMs,
}: Readonly<{
    readonly organizationId: string;
    readonly mode: 'live' | 'sandbox';
    readonly flushDelayMs: number;
    readonly retryDelayMs: number;
    readonly refreshMinIntervalMs: number;
}>): EntityReferenceLabelsStore {
    const resolved = new Map<string, ResolvedReference>();
    const pending = new Set<string>();
    const inFlight = new Set<string>();
    // Keys invalidated while their request was in flight: that response
    // may predate the write, so they go back to pending once it settles.
    const dirty = new Set<string>();
    const listeners = new Set<() => void>();
    let flushHandle: number | null = null;
    let lastResolvedAtMs = 0;
    let disposed = false;

    function splitKey(key: string): readonly [ReferenceType, string] {
        const separator = key.indexOf(':');
        // Keys are only ever built by this store, so the prefix is a type.
        return [
            key.slice(0, separator) as ReferenceType,
            key.slice(separator + 1),
        ];
    }

    function notify(): void {
        for (const listener of listeners) {
            listener();
        }
    }

    function scheduleFlush(delayMs: number): void {
        if (disposed || flushHandle !== null || pending.size === 0) {
            return;
        }
        flushHandle = window.setTimeout(() => {
            flushHandle = null;
            void flush();
        }, delayMs);
    }

    function takeBatch(): Set<string> {
        const batch = new Set<string>();
        const countByType = new Map<ReferenceType, number>();
        for (const key of pending) {
            const [type] = splitKey(key);
            const count = countByType.get(type) ?? 0;
            if (count >= MAX_IDS_PER_TYPE) {
                continue;
            }
            countByType.set(type, count + 1);
            pending.delete(key);
            inFlight.add(key);
            batch.add(key);
        }
        return batch;
    }

    function isSameGroupMember(
        a: ResolvedGroupMember,
        b: ResolvedGroupMember | undefined,
    ): boolean {
        return (
            b !== undefined &&
            a.shiftId === b.shiftId &&
            a.clientId === b.clientId &&
            a.patientName === b.patientName &&
            a.shiftStartTime === b.shiftStartTime &&
            a.shiftEndTime === b.shiftEndTime &&
            a.status === b.status &&
            a.resolution === b.resolution &&
            a.assignedCaregiverId === b.assignedCaregiverId &&
            a.assignedCaregiverName === b.assignedCaregiverName
        );
    }

    function isSameReference(
        a: ResolvedReference,
        b: ResolvedReference,
    ): boolean {
        switch (a.kind) {
            case 'shift':
                return (
                    b.kind === 'shift' &&
                    a.patientName === b.patientName &&
                    a.shiftStartTime === b.shiftStartTime &&
                    a.shiftEndTime === b.shiftEndTime &&
                    a.status === b.status &&
                    a.latestStage === b.latestStage &&
                    a.hasActiveFillOutreach === b.hasActiveFillOutreach &&
                    a.groupId === b.groupId &&
                    a.groupMode === b.groupMode
                );
            case 'caregiver':
                return b.kind === 'caregiver' && a.name === b.name;
            case 'client':
                return (
                    b.kind === 'client' &&
                    a.firstName === b.firstName &&
                    a.lastName === b.lastName
                );
            case 'callout':
                return (
                    b.kind === 'callout' &&
                    a.shiftCount === b.shiftCount &&
                    a.shiftIds.length === b.shiftIds.length &&
                    a.shiftIds.every((id, index) => id === b.shiftIds[index]) &&
                    a.shiftStartTime === b.shiftStartTime &&
                    a.shiftEndTime === b.shiftEndTime
                );
            case 'group':
                return (
                    b.kind === 'group' &&
                    a.outreachId === b.outreachId &&
                    a.groupMode === b.groupMode &&
                    a.ordinal === b.ordinal &&
                    a.openMemberCount === b.openMemberCount &&
                    a.allOrNoneActive === b.allOrNoneActive &&
                    a.members.length === b.members.length &&
                    a.members.every((member, index) =>
                        isSameGroupMember(member, b.members[index]),
                    )
                );
            case 'playbook':
                return b.kind === 'playbook' && a.title === b.title;
            case 'care_plan':
                return (
                    b.kind === 'care_plan' &&
                    a.firstName === b.firstName &&
                    a.lastName === b.lastName
                );
            case 'caregiver_memory':
                return b.kind === 'caregiver_memory' && a.name === b.name;
            case 'client_memory':
                return (
                    b.kind === 'client_memory' &&
                    a.firstName === b.firstName &&
                    a.lastName === b.lastName
                );
            case 'missing':
                return b.kind === 'missing';
            default: {
                const exhaustiveReference: never = a;
                return exhaustiveReference;
            }
        }
    }

    // Keeps the stored object when nothing changed so a refresh that
    // resolved identical data costs zero badge re-renders (the snapshot
    // stays referentially equal).
    function setResolved(key: string, next: ResolvedReference): void {
        // A newer org event named this already-settled key while the batch
        // was in flight. Keep the last settled value until the follow-up
        // batch resolves; first fetches still paint their first response.
        if (dirty.has(key) && resolved.has(key)) {
            return;
        }
        const previous = resolved.get(key);
        if (previous === undefined || !isSameReference(previous, next)) {
            resolved.set(key, next);
        }
    }

    // A key the server omitted (or a doomed batch) settles as missing so it
    // is never refetched; an already-resolved key keeps its last good label.
    function settleUnresolved(keys: Iterable<string>): void {
        for (const key of keys) {
            // A dirty key already has a follow-up batch queued in `finally`,
            // so skip the temporary missing paint here.
            if (dirty.has(key)) {
                continue;
            }
            if (!resolved.has(key)) {
                resolved.set(key, { kind: 'missing' });
            }
        }
    }

    function enqueueMatchingResolved(
        matches: (type: ReferenceType, id: string) => boolean,
        inFlightBehavior: 'replay' | 'skip',
    ): boolean {
        let enqueued = false;
        for (const key of new Set([...resolved.keys(), ...inFlight])) {
            const [type, id] = splitKey(key);
            if (
                resolved.get(key)?.kind === 'missing' ||
                pending.has(key) ||
                !matches(type, id)
            ) {
                continue;
            }
            if (inFlight.has(key)) {
                if (inFlightBehavior === 'replay') {
                    dirty.add(key);
                }
                continue;
            }
            pending.add(key);
            enqueued = true;
        }
        return enqueued;
    }

    function storeBatchResults(
        batch: ReadonlySet<string>,
        response: ResolveResponse,
    ): void {
        for (const shift of response.shifts) {
            setResolved(`shift:${shift.id}`, {
                kind: 'shift',
                patientName: shift.patient_name,
                shiftStartTime: shift.shift_start_time,
                shiftEndTime: shift.shift_end_time,
                status: shift.status,
                latestStage: shift.latest_stage,
                hasActiveFillOutreach: shift.has_active_fill_outreach,
                groupId: shift.group_id ?? null,
                groupMode: shift.group_mode ?? null,
            });
        }
        for (const caregiver of response.caregivers) {
            setResolved(`caregiver:${caregiver.id}`, {
                kind: 'caregiver',
                name: caregiver.name,
            });
        }
        for (const client of response.clients) {
            setResolved(`client:${client.id}`, {
                kind: 'client',
                firstName: client.first_name,
                lastName: client.last_name,
            });
        }
        for (const outreach of response.outreaches) {
            setResolved(`callout:${outreach.id}`, {
                kind: 'callout',
                shiftCount: outreach.shift_count,
                // An older API task in a rolling deploy omits shift_ids;
                // the badge then keeps its count-only presentation.
                shiftIds: outreach.shift_ids ?? [],
                shiftStartTime: outreach.shift_start_time,
                shiftEndTime: outreach.shift_end_time,
            });
        }
        // An older API task in a rolling deploy omits the newer arrays; a
        // bare `for` over undefined throws inside the try and requeues the
        // batch against the same task forever.
        for (const group of response.groups ?? []) {
            setResolved(`group:${group.id}`, {
                kind: 'group',
                outreachId: group.outreach_id,
                groupMode: group.group_mode,
                ordinal: group.ordinal,
                members: group.members.map((member) => ({
                    shiftId: member.shift_id,
                    clientId: member.client_id,
                    patientName: member.patient_name,
                    shiftStartTime: member.shift_start_time,
                    shiftEndTime: member.shift_end_time,
                    status: member.status,
                    resolution: member.resolution,
                    assignedCaregiverId: member.assigned_caregiver_id,
                    assignedCaregiverName: member.assigned_caregiver_name,
                })),
                openMemberCount: group.open_member_count,
                allOrNoneActive: group.all_or_none_active,
            });
        }
        for (const playbook of response.playbooks ?? []) {
            setResolved(`playbook:${playbook.id}`, {
                kind: 'playbook',
                title: playbook.title,
            });
        }
        for (const client of response.care_plans ?? []) {
            setResolved(`care_plan:${client.id}`, {
                kind: 'care_plan',
                firstName: client.first_name,
                lastName: client.last_name,
            });
        }
        for (const caregiver of response.caregiver_memories ?? []) {
            setResolved(`caregiver_memory:${caregiver.id}`, {
                kind: 'caregiver_memory',
                name: caregiver.name,
            });
        }
        for (const client of response.client_memories ?? []) {
            setResolved(`client_memory:${client.id}`, {
                kind: 'client_memory',
                firstName: client.first_name,
                lastName: client.last_name,
            });
        }
        settleUnresolved(batch);
    }

    async function flush(): Promise<void> {
        const batch = takeBatch();
        if (batch.size === 0) {
            return;
        }
        const body: Record<string, Array<string>> = {};
        for (const key of batch) {
            const [type, id] = splitKey(key);
            (body[REQUEST_FIELD_BY_TYPE[type]] ??= []).push(id);
        }
        try {
            const response: ResolveResponse = await getApiClient().post({
                url: '/organizations/{organization_id}/entity-references/resolve',
                pathParams: { organization_id: organizationId },
                mode,
                reason: 'v3-chat-reference-badge',
                body,
                // A pure read: retries can never double-submit, and a
                // rate-limited batch waits out the cooldown silently.
                idempotent: true,
                rateLimitFailureMode: 'silent',
            });
            storeBatchResults(batch, response);
            lastResolvedAtMs = Date.now();
        } catch (error: unknown) {
            // A permanent rejection (auth, validation) would loop the same
            // doomed request forever; settle those ids on the fallback
            // label. Network, rate-limit, and server failures are worth
            // retrying — a badge shows its fallback label meanwhile.
            const isPermanentRejection =
                error instanceof ApiError &&
                !error.isNetworkError &&
                error.status !== undefined &&
                error.status < 500 &&
                error.status !== 429;
            if (isPermanentRejection) {
                settleUnresolved(batch);
                notify();
            } else {
                for (const key of batch) {
                    pending.add(key);
                }
                scheduleFlush(retryDelayMs);
            }
            return;
        } finally {
            let requeued = false;
            for (const key of batch) {
                inFlight.delete(key);
                if (dirty.delete(key)) {
                    pending.add(key);
                    requeued = true;
                }
            }
            if (requeued) {
                scheduleFlush(flushDelayMs);
            }
        }
        notify();
        // Ids past the per-type cap stayed pending; resolve them next.
        scheduleFlush(flushDelayMs);
    }

    return {
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        get: (type, id) => resolved.get(`${type}:${id}`),
        request: (type, id) => {
            const key = `${type}:${id}`;
            if (resolved.has(key) || pending.has(key) || inFlight.has(key)) {
                return;
            }
            pending.add(key);
            scheduleFlush(flushDelayMs);
        },
        // Re-resolves everything already labelled, still as one batch, so a
        // renamed record corrects on window focus without remounting the
        // chat. In-flight keys keep their current request: focus is a
        // best-effort sync, not proof that a write already landed.
        refresh: () => {
            if (Date.now() - lastResolvedAtMs < refreshMinIntervalMs) {
                return;
            }
            if (enqueueMatchingResolved(() => true, 'skip')) {
                scheduleFlush(flushDelayMs);
            }
        },
        // Re-resolves the labelled references `matches` selects, in one
        // batch and without the focus-refresh throttle: an organization
        // event already said they changed. Displayed values stay in place
        // until the response lands, like a refresh.
        invalidate: (matches) => {
            if (enqueueMatchingResolved(matches, 'replay')) {
                scheduleFlush(flushDelayMs);
            }
        },
        dispose: () => {
            disposed = true;
            if (flushHandle !== null) {
                window.clearTimeout(flushHandle);
                flushHandle = null;
            }
        },
    };
}

const EntityReferenceLabelsContext =
    createContext<EntityReferenceLabelsStore | null>(null);

/** What the store actually needs to resolve references. A full
 * `HarnessScope` satisfies it, and hosts without a run (the sidebar's
 * new-chat composer) can provide just these two fields. */
export type EntityReferenceScope = Readonly<{
    readonly organizationId: string;
    readonly mode: 'live' | 'sandbox';
}>;

export function EntityReferenceLabelsProvider({
    scope,
    children,
    flushDelayMs = FLUSH_DELAY_MS,
    retryDelayMs = RETRY_DELAY_MS,
    refreshMinIntervalMs = REFRESH_MIN_INTERVAL_MS,
}: Readonly<{
    readonly scope: EntityReferenceScope;
    readonly children: ReactNode;
    readonly flushDelayMs?: number;
    readonly retryDelayMs?: number;
    readonly refreshMinIntervalMs?: number;
}>): ReactNode {
    const store = useMemo(
        () =>
            createEntityReferenceLabelsStore({
                organizationId: scope.organizationId,
                mode: scope.mode,
                flushDelayMs,
                retryDelayMs,
                refreshMinIntervalMs,
            }),
        [
            scope.organizationId,
            scope.mode,
            flushDelayMs,
            retryDelayMs,
            refreshMinIntervalMs,
        ],
    );
    useEffect(() => store.dispose, [store]);
    useEffect(() => {
        const handleFocus = (): void => store.refresh();
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [store]);
    // Shift chips follow the record live: a shift write names its shift,
    // an EHR sync says only "shifts changed", and an outreach ending or
    // starting flips the outreaching tint without touching the shift row.
    const eventOptions = useMemo(
        () => ({ organizationId: scope.organizationId }),
        [scope.organizationId],
    );
    // Outreach updates are chatty while a callout runs; the sidebar list
    // coalesces them the same way.
    const outreachEventOptions = useMemo(
        () => ({ organizationId: scope.organizationId, throttleMs: 3_000 }),
        [scope.organizationId],
    );
    const isThisMode = (event: OrganizationEvent): boolean =>
        event.mode === undefined ||
        event.mode === null ||
        event.mode === scope.mode;
    useListenForOrganizationEvent(
        'shifts_updated',
        (event) => {
            if (!isThisMode(event)) {
                return;
            }
            const shiftIds = new Set(
                [event.resource, ...event.relationships]
                    .filter((resource) => resource.type === 'shift')
                    .map((resource) => resource.id),
            );
            store.invalidate(
                (type, id) =>
                    type === 'shift' &&
                    (shiftIds.size === 0 || shiftIds.has(id)),
            );
        },
        eventOptions,
    );
    useListenForOrganizationEvent(
        'outreach_updated',
        (event) => {
            if (!isThisMode(event)) {
                return;
            }
            store.invalidate((type) => type === 'shift' || type === 'callout');
        },
        outreachEventOptions,
    );
    return (
        <EntityReferenceLabelsContext.Provider value={store}>
            {children}
        </EntityReferenceLabelsContext.Provider>
    );
}

function formatShiftRange(
    start: string,
    end: string | null,
    timeZone: string,
): string {
    const startLabel = `${formatDateWithWeekday(start, timeZone, 'en-US')}, ${formatTime(start, timeZone, 'en-US')}`;
    // Keep start-only labels readable while an older API is still serving.
    if (!end) {
        return startLabel;
    }
    const endTime = formatTime(end, timeZone, 'en-US');
    const endLabel =
        toDateKey(new Date(start), timeZone) ===
        toDateKey(new Date(end), timeZone)
            ? endTime
            : `${formatDateWithWeekday(end, timeZone, 'en-US')}, ${endTime}`;
    return `${startLabel} – ${endLabel}`;
}

/** Whether a resolve store is already mounted above. Lets nested hosts
 * (the composer inside a run harness) avoid creating a duplicate store. */
export function useHasEntityReferenceStore(): boolean {
    return useContext(EntityReferenceLabelsContext) !== null;
}

/** The resolved reference for one id, requesting it on first use.
 * `null` until the batch resolves (or without a provider/scope). */
export function useResolvedEntityReference(
    scope: EntityReferenceScope | null,
    type: ReferenceType,
    id: string,
): ResolvedReference | null {
    const store = useContext(EntityReferenceLabelsContext);
    // A stable subscribe function, or every render resubscribes the badge.
    const subscribe = useCallback(
        (listener: () => void) => store?.subscribe(listener) ?? noop,
        [store],
    );
    // Server snapshot lets renderToStaticMarkup mount a badge.
    const getSnapshot = useCallback(
        () => store?.get(type, id),
        [store, type, id],
    );
    const reference = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    useEffect(() => {
        if (scope !== null && store !== null && reference === undefined) {
            store.request(type, id);
        }
    }, [scope, store, type, id, reference]);
    if (scope === null || reference === undefined) {
        return null;
    }
    return reference;
}

/** A shift's display fields, or null while unresolved or missing. */
export function useShiftReference(
    scope: HarnessScope | null,
    id: string,
): ResolvedShiftReference | null {
    const reference = useResolvedEntityReference(scope, 'shift', id);
    return reference !== null && reference.kind === 'shift' ? reference : null;
}

/** “Maria’s memory”. Falls back to the bare noun when the record
    resolved without a usable name. */
function possessiveSourceLabel(name: string, noun: string): string {
    const firstName = name.trim().split(' ')[0] ?? '';
    if (firstName === '') {
        return noun.charAt(0).toUpperCase() + noun.slice(1);
    }
    return `${firstName}’s ${noun}`;
}

export function entityReferenceLabel(
    reference: ResolvedReference,
    organizationTimeZone: string,
): string | null {
    switch (reference.kind) {
        case 'shift':
            return `${reference.patientName} · ${formatShiftRange(reference.shiftStartTime, reference.shiftEndTime, organizationTimeZone)}`;
        case 'caregiver':
            return reference.name;
        case 'client':
            return `${reference.firstName} ${reference.lastName}`.trim();
        case 'callout': {
            // The count, not the time range: an outreach spans many shifts,
            // so one range reads as a single shift's schedule and gets long
            // enough to crowd the sentence the pill sits in.
            const count = reference.shiftCount;
            return `Outreach · ${count} ${count === 1 ? 'shift' : 'shifts'}`;
        }
        case 'group':
            return groupReferenceLabel({
                clientNames: [
                    ...new Set(
                        reference.members
                            .map((member) => member.patientName)
                            .filter((name) => name !== ''),
                    ),
                ],
                shiftCount: reference.members.length,
                groupMode: reference.groupMode,
            });
        // A person-scoped source is named by its person: two memories
        // cited in one paragraph are only distinguishable that way.
        case 'playbook':
            return 'Playbook';
        case 'care_plan':
            return possessiveSourceLabel(reference.firstName, 'care plan');
        case 'caregiver_memory':
            return possessiveSourceLabel(reference.name, 'memory');
        case 'client_memory':
            return possessiveSourceLabel(reference.firstName, 'memory');
        case 'missing':
            return null;
        default: {
            const exhaustiveReference: never = reference;
            return exhaustiveReference;
        }
    }
}

export function useEntityReferenceLabel(
    scope: HarnessScope | null,
    type: ReferenceType,
    id: string,
): string | null {
    const reference = useResolvedEntityReference(scope, type, id);
    if (scope === null || reference === null) {
        return null;
    }
    return entityReferenceLabel(reference, scope.organizationTimeZone);
}

/** Labels already resolved in the store, read without requesting. */
export function useEntityReferenceLabelReader(
    scope: HarnessScope | null,
): (type: ReferenceType, id: string) => string | null {
    const store = useContext(EntityReferenceLabelsContext);
    return useCallback(
        (type: ReferenceType, id: string) => {
            const reference = store?.get(type, id);
            if (scope === null || reference === undefined) {
                return null;
            }
            return entityReferenceLabel(reference, scope.organizationTimeZone);
        },
        [store, scope],
    );
}

/** Which playbook rule, whose care plan, whose memory. The chip names only
    the source; this is what its hover card adds, and for the two memory
    types it is also the subject name the modal titles itself with. */
export function useEntityReferenceCitationDetail(
    scope: HarnessScope | null,
    type: ReferenceType,
    id: string,
): string | null {
    const reference = useResolvedEntityReference(scope, type, id);
    if (reference === null) {
        return null;
    }
    switch (reference.kind) {
        case 'playbook':
            return reference.title.trim() || null;
        case 'care_plan':
        case 'client_memory':
            return (
                `${reference.firstName} ${reference.lastName}`.trim() || null
            );
        case 'caregiver_memory':
            return reference.name.trim() || null;
        default:
            return null;
    }
}
